import { NOTE_STYLES, type NoteKind } from '@galley/shared'
import { useEffect, type CSSProperties, type ElementType, type ReactNode } from 'react'
import { useT } from '../../i18n'
import { cx } from '../cx'
import { loadNoteFont } from '../fonts'
import { Icon, isIconName, type IconName } from '../icons'
import styles from './NoteCard.module.css'

const FALLBACK_ICONS: Record<NoteKind, IconName> = {
  glow: 'sparkle',
  grow: 'sprout',
  wonder: 'question',
  see: 'eye-see',
  think: 'bulb',
}

/**
 * Each note kind has an icon as well as a colour and a shape (colour is never the only cue).
 * NOTE_STYLES in @galley/shared names the icon; the fallback covers a name this set lacks.
 */
export const NOTE_ICONS = Object.fromEntries(
  (Object.keys(FALLBACK_ICONS) as NoteKind[]).map((kind) => {
    const named = NOTE_STYLES[kind].icon
    return [kind, isIconName(named) ? named : FALLBACK_ICONS[kind]]
  }),
) as Record<NoteKind, IconName>

export interface NoteCardProps {
  kind: NoteKind
  /** The note text (student writing: rendered as plain text). */
  body: ReactNode
  /**
   * Sentence starter the writer picked. The body is always the full note (SPEC §3.5), so the
   * starter is only used to set the opening words in the starter style when the body begins
   * with it; it is never printed twice.
   */
  starter?: string | null
  /** Where the note points ("Paragraph 3", "0:42"). */
  anchor?: ReactNode
  /** Byline, endorsement or actions. */
  footer?: ReactNode
  /** Resting tilt in degrees (seeded by the caller; keep within ±2). */
  tilt?: number
  as?: ElementType
  lang?: string
  className?: string
}

/**
 * A sticky note. Glow: solid border. Grow: folded corner. Wonder: dashed thought bubble.
 * The kind word and icon always show. Colours come from NOTE_STYLES and are the same in
 * every theme, like traffic signs.
 */
export function NoteCard({
  kind,
  body,
  starter,
  anchor,
  footer,
  tilt = 0,
  as: Tag = 'article',
  lang,
  className,
}: NoteCardProps) {
  const t = useT()
  useEffect(() => {
    void loadNoteFont()
  }, [])
  const style = { '--note-tilt': `${tilt}deg` } as CSSProperties
  return (
    <Tag className={cx(styles.note, styles[kind], className)} style={style} data-note-kind={kind}>
      <header className={styles.kind}>
        <Icon name={NOTE_ICONS[kind]} size={20} className={styles.icon} />
        <span>{t.common.notes[kind]}</span>
        {anchor && <span className={styles.anchor}>{anchor}</span>}
      </header>
      <p className={styles.body} lang={lang} data-reading-text="">
        {typeof body === 'string' && starter && body.startsWith(starter) ? (
          <>
            <span className={styles.starter}>{starter}</span>
            {body.slice(starter.length)}
          </>
        ) : (
          body
        )}
      </p>
      {footer && <footer className={styles.footer}>{footer}</footer>}
    </Tag>
  )
}
